import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, TrendingDown, Calculator, AlertTriangle, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type DealGrade = 'A' | 'B' | 'C' | 'D'
type AnalyzerTab = 'flip' | 'brrr'

type FlipInputs = {
  purchasePrice: number; rehabCost: number; arv: number
  holdingMonths: number; loanRate: number; downPct: number
  agentCommission: number; closingCostPct: number
}

type BRRRInputs = {
  purchasePrice: number; rehabCost: number; arv: number
  refinanceLtv: number; newLoanRate: number; loanTermYears: number
  monthlyRent: number; monthlyExpenses: number
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
const pct = (n: number) => `${n.toFixed(1)}%`

function grade(roi: number): DealGrade {
  if (roi >= 20) return 'A'
  if (roi >= 15) return 'B'
  if (roi >= 10) return 'C'
  return 'D'
}

const GRADE_STYLE: Record<DealGrade, string> = {
  A: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-800',
  B: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-800',
  C: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800',
  D: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-200 dark:border-rose-800',
}

const GRADE_ADVICE: Record<DealGrade, string> = {
  A: 'Excellent deal — proceed with confidence.',
  B: 'Good deal — solid returns with manageable risk.',
  C: 'Marginal — negotiate a lower purchase price or reduce rehab scope.',
  D: 'Poor deal — avoid unless strategic reason (e.g., portfolio anchor).',
}

// ─── Number Field ─────────────────────────────────────────────────────────────

function NumberField({ label, value, onChange, prefix, suffix, hint }:
  { label: string; value: number; onChange: (v: number) => void; prefix?: string; suffix?: string; hint?: string }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">{label}</label>
      <div className="relative">
        {prefix && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{prefix}</span>}
        <Input
          type="number"
          value={value || ''}
          onChange={e => onChange(Number(e.target.value) || 0)}
          className={cn('h-10 text-sm', prefix && 'pl-7', suffix && 'pr-8')}
        />
        {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{suffix}</span>}
      </div>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  )
}

// ─── Result Card ──────────────────────────────────────────────────────────────

function ResultCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'up' | 'down' | 'neutral' }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={cn('mt-1 text-xl font-bold leading-none',
        tone === 'up' ? 'text-emerald-700 dark:text-emerald-400' :
        tone === 'down' ? 'text-rose-700 dark:text-rose-400' :
        'text-slate-900 dark:text-slate-50'
      )}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  )
}

// ─── Flip Analyzer ────────────────────────────────────────────────────────────

function FlipCalc() {
  const [inputs, setInputs] = useState<FlipInputs>({
    purchasePrice: 185000, rehabCost: 38000, arv: 285000,
    holdingMonths: 6, loanRate: 8.5, downPct: 20,
    agentCommission: 6, closingCostPct: 1.5,
  })

  const set = (k: keyof FlipInputs) => (v: number) => setInputs(prev => ({ ...prev, [k]: v }))

  const calc = useMemo(() => {
    const loanAmount = inputs.purchasePrice * (1 - inputs.downPct / 100)
    const monthlyRate = inputs.loanRate / 100 / 12
    const monthlyInterest = loanAmount * monthlyRate
    const monthlyHoldCost = monthlyInterest + (inputs.purchasePrice * 0.001) + 200 // interest + insurance + utilities
    const totalHoldingCost = monthlyHoldCost * inputs.holdingMonths

    const totalIn = inputs.purchasePrice + inputs.rehabCost + totalHoldingCost
    const cashInvested = (inputs.purchasePrice * inputs.downPct / 100) + inputs.rehabCost + totalHoldingCost
    const sellingCosts = inputs.arv * (inputs.agentCommission / 100) + inputs.arv * (inputs.closingCostPct / 100)
    const netProfitCalc = inputs.arv - totalIn - sellingCosts
    const roi = cashInvested > 0 ? (netProfitCalc / cashInvested) * 100 : 0
    const breakEvenPrice = totalIn + sellingCosts
    const dealGrade = grade(roi)
    const annualizedRoi = inputs.holdingMonths > 0 ? (roi / inputs.holdingMonths) * 12 : 0

    return {
      totalIn, cashInvested, sellingCosts, netProfit: netProfitCalc,
      roi, annualizedRoi, breakEvenPrice, dealGrade, monthlyHoldCost, arv: inputs.arv,
    }
  }, [inputs])

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {/* Inputs */}
      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base">Deal Inputs</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NumberField label="Purchase Price" value={inputs.purchasePrice} onChange={set('purchasePrice')} prefix="$" />
          <NumberField label="Rehab Cost" value={inputs.rehabCost} onChange={set('rehabCost')} prefix="$" />
          <NumberField label="After Repair Value (ARV)" value={inputs.arv} onChange={set('arv')} prefix="$" hint="Comp-based estimate" />
          <NumberField label="Holding Period" value={inputs.holdingMonths} onChange={set('holdingMonths')} suffix="mo" hint="Time to flip" />
          <NumberField label="Loan Rate" value={inputs.loanRate} onChange={set('loanRate')} suffix="%" hint="Hard money or conventional" />
          <NumberField label="Down Payment" value={inputs.downPct} onChange={set('downPct')} suffix="%" />
          <NumberField label="Agent Commission" value={inputs.agentCommission} onChange={set('agentCommission')} suffix="%" hint="Buyer + seller side" />
          <NumberField label="Closing Costs" value={inputs.closingCostPct} onChange={set('closingCostPct')} suffix="%" hint="Title, escrow, transfer" />
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-4">
        <Card className="shadow-sm border-2" style={{ borderColor: calc.dealGrade === 'A' ? '#10b981' : calc.dealGrade === 'B' ? '#3b82f6' : calc.dealGrade === 'C' ? '#f59e0b' : '#ef4444' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Deal Grade</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={cn('text-4xl font-black rounded-xl px-3 py-1 border-2', GRADE_STYLE[calc.dealGrade])}>{calc.dealGrade}</span>
                  <div>
                    <p className="text-lg font-bold text-slate-900 dark:text-slate-50">{pct(calc.roi)} ROI</p>
                    <p className="text-xs text-slate-500">{pct(calc.annualizedRoi)} annualized</p>
                  </div>
                </div>
              </div>
              {calc.netProfit >= 0 ? <TrendingUp className="h-8 w-8 text-emerald-600" /> : <TrendingDown className="h-8 w-8 text-rose-600" />}
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 italic">{GRADE_ADVICE[calc.dealGrade]}</p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <ResultCard label="Net Profit" value={fmt(calc.netProfit)} sub="After all costs" tone={calc.netProfit >= 0 ? 'up' : 'down'} />
          <ResultCard label="Cash Invested" value={fmt(calc.cashInvested)} sub="Your equity at risk" tone="neutral" />
          <ResultCard label="Total Project Cost" value={fmt(calc.totalIn)} sub="Purchase + Rehab + Hold" tone="neutral" />
          <ResultCard label="Selling Costs" value={fmt(calc.sellingCosts)} sub="Commission + closing" tone="down" />
          <ResultCard label="Monthly Hold Cost" value={fmt(calc.monthlyHoldCost)} sub="Interest + ins + utils" tone="down" />
          <ResultCard label="Break-Even Price" value={fmt(calc.breakEvenPrice)} sub={`ARV needs to be >${fmt(calc.breakEvenPrice)}`} tone="neutral" />
        </div>

        <Card className={cn('border-l-4 shadow-sm', calc.arv - calc.breakEvenPrice > 30000 ? 'border-l-emerald-500' : 'border-l-amber-500')}>
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">ARV Margin to Break-Even</p>
            <p className={cn('text-lg font-bold mt-1', calc.arv - calc.breakEvenPrice > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700')}>
              {fmt(calc.arv - calc.breakEvenPrice)} buffer
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {calc.arv > calc.breakEvenPrice
                ? `ARV can drop ${fmt(calc.arv - calc.breakEvenPrice)} before you break even`
                : 'ARV is below break-even — deal loses money'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ─── BRRR Analyzer ────────────────────────────────────────────────────────────

function BRRRAnalyzer() {
  const [inputs, setInputs] = useState<BRRRInputs>({
    purchasePrice: 185000, rehabCost: 38000, arv: 265000,
    refinanceLtv: 75, newLoanRate: 7.0, loanTermYears: 30,
    monthlyRent: 2200, monthlyExpenses: 800,
  })

  const set = (k: keyof BRRRInputs) => (v: number) => setInputs(prev => ({ ...prev, [k]: v }))

  const calc = useMemo(() => {
    const totalInvested = inputs.purchasePrice + inputs.rehabCost
    const refinanceLoan = inputs.arv * (inputs.refinanceLtv / 100)
    const cashOutAfterRefi = refinanceLoan - inputs.purchasePrice

    // Monthly payment
    const r = (inputs.newLoanRate / 100) / 12
    const n = inputs.loanTermYears * 12
    const monthlyPayment = r === 0 ? refinanceLoan / n : refinanceLoan * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)

    const cashLeftInDeal = totalInvested - refinanceLoan
    const isTrueBRRR = cashLeftInDeal <= 0

    const monthlyNOI = inputs.monthlyRent - inputs.monthlyExpenses
    const monthlyCashFlow = monthlyNOI - monthlyPayment
    const annualCashFlow = monthlyCashFlow * 12

    const equityCapture = inputs.arv - totalInvested
    const cocROI = cashLeftInDeal > 0 ? (annualCashFlow / cashLeftInDeal) * 100 : 999

    const capRate = inputs.arv > 0 ? ((inputs.monthlyRent - inputs.monthlyExpenses) * 12 / inputs.arv) * 100 : 0
    const dealGrade = grade(cocROI > 99 ? 30 : cocROI)

    return {
      totalInvested, refinanceLoan, cashOutAfterRefi, cashLeftInDeal, isTrueBRRR,
      monthlyPayment, monthlyCashFlow, annualCashFlow, equityCapture, cocROI, capRate, dealGrade,
    }
  }, [inputs])

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base">BRRR Inputs</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NumberField label="Purchase Price" value={inputs.purchasePrice} onChange={set('purchasePrice')} prefix="$" />
          <NumberField label="Rehab Cost" value={inputs.rehabCost} onChange={set('rehabCost')} prefix="$" />
          <NumberField label="ARV (After Repair Value)" value={inputs.arv} onChange={set('arv')} prefix="$" hint="Post-rehab appraised value" />
          <NumberField label="Refinance LTV" value={inputs.refinanceLtv} onChange={set('refinanceLtv')} suffix="%" hint="Typical: 70–80%" />
          <NumberField label="New Loan Rate" value={inputs.newLoanRate} onChange={set('newLoanRate')} suffix="%" />
          <NumberField label="Loan Term" value={inputs.loanTermYears} onChange={set('loanTermYears')} suffix="yr" />
          <NumberField label="Monthly Rent" value={inputs.monthlyRent} onChange={set('monthlyRent')} prefix="$" />
          <NumberField label="Monthly Expenses" value={inputs.monthlyExpenses} onChange={set('monthlyExpenses')} prefix="$" hint="Tax + ins + mgmt + repairs" />
        </CardContent>
      </Card>

      <div className="space-y-4">
        <Card className={cn('border-2 shadow-sm', calc.isTrueBRRR ? 'border-emerald-500' : 'border-amber-400')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">BRRR Verdict</p>
                <p className={cn('text-2xl font-black mt-1', calc.isTrueBRRR ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400')}>
                  {calc.isTrueBRRR ? '✓ True BRRR' : '⚠ Partial BRRR'}
                </p>
              </div>
              {calc.isTrueBRRR
                ? <CheckCircle className="h-8 w-8 text-emerald-600" />
                : <AlertTriangle className="h-8 w-8 text-amber-500" />}
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              {calc.isTrueBRRR
                ? `You recoup all ${fmt(calc.totalInvested)} invested and still pull ${fmt(Math.abs(calc.cashLeftInDeal))} cash back.`
                : `You still have ${fmt(calc.cashLeftInDeal)} left in the deal after refinancing. Reduce costs or increase ARV.`}
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <ResultCard label="Refi Loan Amount" value={fmt(calc.refinanceLoan)} sub={`${inputs.refinanceLtv}% of ${fmt(inputs.arv)} ARV`} tone="neutral" />
          <ResultCard label="Cash Left in Deal" value={fmt(Math.max(0, calc.cashLeftInDeal))} sub={calc.isTrueBRRR ? 'Full cash recovery' : 'Equity trapped'} tone={calc.isTrueBRRR ? 'up' : 'down'} />
          <ResultCard label="Monthly Cash Flow" value={fmt(calc.monthlyCashFlow)} sub="After debt service" tone={calc.monthlyCashFlow >= 0 ? 'up' : 'down'} />
          <ResultCard label="Annual Cash Flow" value={fmt(calc.annualCashFlow)} sub="Cash-on-cash base" tone={calc.annualCashFlow >= 0 ? 'up' : 'down'} />
          <ResultCard label="Equity Captured" value={fmt(calc.equityCapture)} sub="ARV minus total invested" tone="up" />
          <ResultCard label="Cap Rate" value={pct(calc.capRate)} sub="NOI / ARV" tone="neutral" />
          <ResultCard label="CoC ROI" value={calc.cocROI > 99 ? '∞ (infinite)' : pct(calc.cocROI)} sub="Annual CF / cash left in" tone="up" />
          <ResultCard label="Monthly Payment" value={fmt(calc.monthlyPayment)} sub={`${inputs.newLoanRate}% / ${inputs.loanTermYears}yr`} tone="down" />
        </div>

        {calc.isTrueBRRR && (
          <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20 shadow-sm">
            <CardContent className="p-3 flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                This is a textbook BRRR. You recover 100% of invested capital, own the property free-and-clear of your own money, and collect <strong>{fmt(calc.annualCashFlow)}/year</strong> on infinite ROI.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function DealAnalyzerModule() {
  const [tab, setTab] = useState<AnalyzerTab>('flip')

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Deal Analyzer</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">Analyze flips, BRRR deals, and rental investments</p>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        <button onClick={() => setTab('flip')} className={cn(
          'flex items-center gap-1.5 flex-1 justify-center rounded-lg px-3 py-2 text-sm font-medium transition-all',
          tab === 'flip' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/60'
        )}>
          <TrendingUp className="h-4 w-4" />Flip Calculator
        </button>
        <button onClick={() => setTab('brrr')} className={cn(
          'flex items-center gap-1.5 flex-1 justify-center rounded-lg px-3 py-2 text-sm font-medium transition-all',
          tab === 'brrr' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/60'
        )}>
          <Calculator className="h-4 w-4" />BRRR Analyzer
        </button>
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
        {tab === 'flip' ? <FlipCalc /> : <BRRRAnalyzer />}
      </motion.div>
    </div>
  )
}
